import { useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import { Routes } from "router/routes";
import { CaretDownIcon } from "assets/website";
import { creditFaqItems } from "../pricing/faq";

interface FaqItem {
  question: string;
  answer: ReactNode;
}

interface FaqCategory {
  id: string;
  label: string;
  items: FaqItem[];
}

const categories: FaqCategory[] = [
  {
    id: "pricing",
    label: "Pricing",
    items: [
      ...creditFaqItems,
      {
        question: "Do you offer a custom or enterprise package?",
        answer:
          "Yes. For organizations with high hiring volumes or custom assessment needs, we provide tailored credit bundles and premium support — get in touch and we'll put one together.",
      },
    ],
  },
  {
    id: "technical",
    label: "Technical",
    items: [
      {
        question: "How does this improve our hiring speed?",
        answer:
          "Precision automates CV screening, ranking, and competence assessment, so your team spends less time sifting through applications and more time engaging the strongest candidates. Shortlists that used to take days are ready in minutes.",
      },
      {
        question: "What does Precision actually measure?",
        answer:
          "Precision evaluates candidates across multiple dimensions, technical skills, soft skills, work style, workplace qualities, work motivators, personality, and domain knowledge, then maps each one against the specific demands of the role.",
      },
      {
        question: "Can it be customized for our specific roles?",
        answer:
          "Yes. Assessments are generated around the exact role you are hiring for, using your job description and requirements so candidates are evaluated on the capabilities that matter for that position.",
      },
      {
        question: "What do we receive after candidates complete the assessment?",
        answer:
          "You get a detailed, multi-dimensional report for every candidate, complete with capability scores, strengths and gaps, predictive fit scoring, and side-by-side comparisons to support a confident hiring decision.",
      },
      {
        question: "How is Precision different from other assessment tools?",
        answer:
          "Most tools test isolated skills. Precision evaluates real execution capability against the role, combining CV analysis, role-based assessments, and predictive fit scoring into one structured, comparable report.",
      },
    ],
  },
  {
    id: "general",
    label: "General",
    items: [
      {
        question: "What is Precision?",
        answer:
          "Precision is a role-based hiring intelligence platform that helps teams evaluate candidates beyond CVs and interviews, so you can understand not just who a candidate is, but how they will actually perform in the role.",
      },
      {
        question: "Who is Precision built for?",
        answer:
          "Precision is built for founders, HR and talent teams, recruiters and agencies, and institutions, anyone who needs consistent, structured, and fair evaluation across roles and departments.",
      },
      {
        question: "How does Precision AI work?",
        answer:
          "Precision analyzes each candidate against your role requirements, mapping skills to experience and generating competence assessments automatically. It then scores fit and produces clear, comparable reports for every candidate.",
      },
      {
        question: "What kinds of assessments does Precision provide?",
        answer:
          "Precision provides role-specific assessments covering technical skills, soft skills, work ethics, work style, and domain knowledge, all tailored to the role you are hiring for.",
      },
      {
        question: "Can I export or share candidate reports?",
        answer:
          "Yes. Reports can be shared with reviewers and stakeholders, allowing your team to add comments, compare candidates, and decide together.",
      },
    ],
  },
];

const FaqUI = () => {
  const navigate = useNavigate();
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>(categories[0].id);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    window.scrollTo(0, 0);
    AOS.init({ duration: 1000, offset: 100, easing: "ease-in-out", once: true });
    AOS.refresh();
  }, []);

  // Scroll-spy: highlight the category currently in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveCategory(entry.target.id);
          }
        });
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 }
    );

    Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const scrollToCategory = (id: string) => {
    const el = sectionRefs.current[id];
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 140;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const toggle = (key: string) => setOpenKey(openKey === key ? null : key);

  return (
    <div className="bg-white">
      {/* ────── Hero ────── */}
      <section className="pt-36 md:pt-44 pb-10 md:pb-16">
        <div className="max-w-[1024px] mx-auto lg:px-8 px-6 text-center">
          <h1
            data-aos="fade-up"
            className="text-3xl md:text-5xl lg:text-6xl font-display font-bold text-[#050B17]"
          >
            Frequently Asked Questions
          </h1>
        </div>
      </section>

      {/* ────── Body ────── */}
      <section className="pb-16 md:pb-24">
        <div className="max-w-[1100px] mx-auto lg:px-8 px-6">
          <div className="grid grid-cols-1 lg:grid-cols-[180px_1fr] gap-8 lg:gap-16 items-start">
            {/* Sidebar category nav */}
            <nav className="hidden lg:block sticky top-32 self-start max-h-[calc(100vh-9rem)] overflow-y-auto space-y-4">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => scrollToCategory(cat.id)}
                  className={`block text-left text-lg transition-colors ${
                    activeCategory === cat.id
                      ? "text-[#012169] font-semibold"
                      : "text-[#98A2B3] font-normal hover:text-[#475467]"
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </nav>

            {/* Categories + accordions */}
            <div className="space-y-12 md:space-y-16">
              {categories.map((cat) => (
                <div
                  key={cat.id}
                  id={cat.id}
                  ref={(el) => (sectionRefs.current[cat.id] = el)}
                  className="scroll-mt-32"
                >
                  <h2 className="text-xl md:text-2xl font-display font-bold text-[#012169] mb-5">
                    {cat.label}
                  </h2>
                  <div className="space-y-3 md:space-y-4">
                    {cat.items.map((item, index) => {
                      const key = `${cat.id}-${index}`;
                      const isOpen = openKey === key;
                      return (
                        <div
                          key={key}
                          className="bg-[#F2F4F7] rounded-xl overflow-hidden"
                        >
                          <button
                            onClick={() => toggle(key)}
                            className="w-full px-4 md:px-6 py-4 md:py-5 flex items-center justify-between text-left"
                          >
                            <span className="text-[#1D2939] pr-3 md:pr-4 text-base md:text-lg">
                              {item.question}
                            </span>
                            <CaretDownIcon
                              className={`w-4 h-4 md:w-5 md:h-5 text-[#1D2939] flex-shrink-0 transition-transform duration-200 ${
                                isOpen ? "rotate-180" : ""
                              }`}
                            />
                          </button>
                          <div
                            className={`overflow-hidden transition-all duration-300 ${
                              isOpen ? "max-h-96" : "max-h-0"
                            }`}
                          >
                            <div className="mx-4 md:mx-6 border-t border-[#D0D5DD]" />
                            <div className="px-4 md:px-6 py-4 text-sm md:text-base text-neutral-600 leading-relaxed">
                              {item.answer}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ────── Contact CTA ────── */}
      <section className="pb-20 md:pb-28">
        <div className="max-w-[1024px] mx-auto px-6 text-center">
          <h2 className="text-xl md:text-2xl lg:text-3xl font-display font-bold text-[#050B17] mb-6">
            Still confused about something?
          </h2>
          <button
            onClick={() => navigate(Routes.contactUs)}
            className="px-8 py-4 bg-[#012169] hover:bg-[#01154d] text-base text-white font-semibold rounded-lg transition-all duration-200 hover:shadow-lg"
          >
            Contact Us
          </button>
        </div>
      </section>
    </div>
  );
};

export { FaqUI };
